import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
    /**
     * @type {Map<number, string>}
     */
    pages = new Map();

    /** @type {IntersectionObserver | undefined} */
    infinityScrollObserver;

    /** @type {((value: void) => void) | null} */
    #resolveNextPagePromise = null;

    /** @type {((value: void) => void) | null} */
    #resolvePreviousPagePromise = null;

    connectedCallback() {
        super.connectedCallback();
        this.#GetMaxPages();
        this.#loadFromCash();
        this.#fetchPage('next');
        this.#fetchPage('previous');
        this.#observeViewMore();
        this.#initSaveState();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.infinityScrollObserver) {
            this.infinityScrollObserver.disconnect();
        }
    }

    #initSaveState() {
      document.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        const link = e.target.closest('.grid__item a');
        const productGrid = document.querySelector('#product-grid');

        if (!link || !(productGrid instanceof HTMLElement)) return;

        sessionStorage.setItem('scrollPosition', window.scrollY.toString());
        sessionStorage.setItem('collectionHTML', productGrid.innerHTML);
        sessionStorage.setItem('collectionURL', window.location.href);
      });
    }

    #GetMaxPages() {
      const container = document.querySelector('#product-grid');
      if (!(container instanceof HTMLElement)) return;

      this.maxPages = container.dataset.lastPage;
    }

    #loadFromCash() {
      const savedHTML = sessionStorage.getItem('collectionHTML');
      const savedScroll = sessionStorage.getItem('scrollPosition') || '';
      const savedURL = sessionStorage.getItem('collectionURL');

      if (savedHTML && savedURL === window.location.href) {
        const container = document.querySelector('#product-grid');
        if (container) {
          container.innerHTML = savedHTML;

          setTimeout(() => {
            window.scrollTo(0, parseInt(savedScroll, 10));
          }, 50);

          sessionStorage.removeItem('collectionHTML');
          sessionStorage.removeItem('scrollPosition');
          sessionStorage.removeItem('collectionURL');

          this.refreshRefr()
        }
      }
    }

    #observeViewMore() {
        const { viewMorePrevious, viewMoreNext } = this.refs;
        if (!viewMorePrevious || !viewMoreNext) return;

        this.infinityScrollObserver = new IntersectionObserver(

            async (entries) => {
                // Wait for any in-progress view transitions to finish
                if (viewTransition.current) await viewTransition.current;

                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        if (entry.target === viewMorePrevious) {
                            this.#renderPreviousPage();
                        } else {
                            this.#renderNextPage();
                        }
                    }
                }
            },
            {
                rootMargin: '100px',
            }
        );

        this.infinityScrollObserver.observe(viewMorePrevious);
        this.infinityScrollObserver.observe(viewMoreNext);
    }

    /**
     * @param {Object} pageInfo - The page info
     * @param {number} pageInfo.page - The page number
     * @returns {boolean} Whether to use the page
     */
    #shouldUsePage(pageInfo) {
        if (!pageInfo) return false;
        if (pageInfo.page < 1) return false;

        return true;
    }

    /**
     * @param {"previous" | "next"} type
     */
    async #fetchPage(type) {
        const page = this.#getPage(type);

        if (!page || !this.#shouldUsePage(page)) return;
        const pageUrl = new URL(page.url);
        pageUrl.searchParams.set('page', String(page.page));
        await this.#fetchSpecificPage(page.page, pageUrl);

        if (type === 'next') {
            this.#resolveNextPagePromise?.();
            this.#resolveNextPagePromise = null;
        } else {
            this.#resolvePreviousPagePromise?.();
            this.#resolvePreviousPagePromise = null;
        }
    }

    /**
     * @param {number} pageNumber - The page number to fetch
     * @param {URL} [url] - Optional URL, will be constructed if not provided
     */
    async #fetchSpecificPage(pageNumber, url = undefined) {
        const pageInfo = { page: pageNumber, url };

        if (!url) {
            const newUrl = new URL(window.location.href);
            newUrl.hash = '';
            pageInfo.url = newUrl;
        }

        if (!this.#shouldUsePage(pageInfo)) return;
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
        this.pages.set(pageNumber, pageContent);
    }

    async #renderNextPage() {
        const { grid } = this.refs;

        if (!grid) return;

        const nextPage = this.#getPage('next');
        if (!nextPage || !this.#shouldUsePage(nextPage)) return;
        let nextPageItemElements = this.#getGridForPage(nextPage.page);

        if (!nextPageItemElements) {
            const promise = new Promise((res) => {
                this.#resolveNextPagePromise = res;
            });
            await promise;
            nextPageItemElements = this.#getGridForPage(nextPage.page);
            if (!nextPageItemElements) return;
        }

        grid.append(...nextPageItemElements);


        history.pushState('', '', nextPage.url.toString());

        requestIdleCallback(() => {
            this.#fetchPage('next');
        });
    }

    async #renderPreviousPage() {
        const { grid } = this.refs;

        if (!grid) return;

        const previousPage = this.#getPage('previous');
        if (!previousPage || !this.#shouldUsePage(previousPage)) return;

        let previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
            const promise = new Promise((res) => {
                this.#resolvePreviousPagePromise = res;
            });
            await promise;
            previousPageItemElements = this.#getGridForPage(previousPage.page);
            if (!previousPageItemElements) return;
        }

        // Store the current scroll position and height of the first element
        const scrollTop = window.scrollY;
        const firstElement = grid.firstElementChild;
        const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

        // Prepend the new elements
        grid.prepend(...previousPageItemElements);
        history.pushState('', '', previousPage.url.toString());

        // Calculate and adjust scroll position to maintain the same view
        if (firstElement) {
            const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
            const heightDiff = newHeight - oldHeight;
            window.scrollTo({
                top: scrollTop + heightDiff,
                behavior: 'instant',
            });
        }

        requestIdleCallback(() => {
            this.#fetchPage('previous');
        });
    }


    /**
     * @param {"previous" | "next"} type
     * @returns {{ page: number, url: URL } | undefined}
     */
    #getPage(type) {
        const { cards } = this.refs;
        const isPrevious = type === 'previous';

        if (!Array.isArray(cards)) return;

        const targetCard = cards[isPrevious ? 0 : cards.length - 1];

        if (!targetCard) return;

        const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

        if (page > Number(this.maxPages)) return;

        const url = new URL(window.location.href);
        url.hash = '';

        return {
            page,
            url,
        };
    }

    /**
     * @param {number} page
     * @returns {NodeListOf<Element> | undefined}
     */
    #getGridForPage(page) {
        const pageHTML = this.pages.get(page);

        if (!pageHTML) return;

        const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
        const gridElement = parsedPage.querySelector('[ref="grid"]');
        if (!gridElement) return;
        return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    }

    get sectionId() {
        const id = this.getAttribute('section-id');

        if (!id) throw new Error('The section-id attribute is required');

        return id;
    }
}

customElements.define('paginated-list', PaginatedList);
